import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { sortLocalized, type LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { MeetingType } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  /** İşlem süren satır — anahtar ve sil düğmesi kilitlenir (çift istek olmasın). */
  busyId: number | null
  onEdit: (row: MeetingType) => void
  onToggle: (row: MeetingType, active: boolean) => void
  onDelete: (row: MeetingType) => void
  /** 126 — Kısıtlı Yetkili asistan: işlem kolonu hiç çizilmez; durum, rozet kolonundan okunur. */
  readOnly?: boolean
}

/**
 * Toplantı Türü tablosunun sütunları (paylaşılan `ui/DataTable`'a beslenir): Ad (TR) · Ad (EN) ·
 * Durum rozeti · satır aksiyonları (Aktif/Pasif anahtarı, düzenle, sil).
 *
 * <p><b>Sıra kolonu YOKTUR</b> — kardeş lookup ekranlarındaki ↑/↓ okları burada bilinçle yok: tabloda
 * `SORT_INDEX` kolonu bulunmuyor, liste ada göre geliyor. Korumalı sistem kaydı da yoktur.
 */
export function meetingTypeColumns({
  t,
  localized,
  busyId,
  onEdit,
  onToggle,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const columns = [
    <Column
      key="name"
      header={t('meetingTypes.columns.name')}
      // Sıralama GÖRÜNEN değere göre; `field` verilseydi İngilizce arayüzde TR adına göre
      // sıralanır, yani başlık kullanıcıya yanlış söylerdi.
      sortable
      sortFunction={(e) =>
        sortLocalized(e.data as MeetingType[], localized, (r) => [r.nameTr, r.nameEn], e.order)
      }
      body={(row: MeetingType) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('meetingTypes.columns.status')}
      style={{ width: '8rem' }}
      body={(row: MeetingType) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('meetingTypes.status.active') : t('meetingTypes.status.passive')}
          </Badge>
        )
      }}
    />,
  ]
  if (readOnly) return columns
  return [
    ...columns,
    <Column
      key="actions"
      header={t('meetingTypes.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: MeetingType) => (
        <div className="admin-row-actions">
          <InputSwitch
            className="admin-switch"
            checked={row.recordStatus === 1}
            disabled={busyId === row.id}
            onChange={(e) => onToggle(row, Boolean(e.value))}
            aria-label={row.nameTr}
            tooltip={t('meetingTypes.actions.toggle')}
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('meetingTypes.actions.editNamed', { name: row.nameTr })}
            tooltip={t('meetingTypes.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            aria-label={t('meetingTypes.actions.deleteNamed', { name: row.nameTr })}
            tooltip={t('meetingTypes.actions.delete')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onDelete(row)}
          />
        </div>
      )}
    />,
  ]
}
